<template>
  <el-card
    id="attendanceCard"
    v-loading="loading"
    class="clear-card attendanceCard"
    :class="{
      detailsView: isDetailsView,
      listView: !isDetailsView,
      mobileMode: ['mobile', 'tablet'].includes($mq),
      deskMode: ['desktop', 'laptop'].includes($mq)
    }"
    shadow="hover"
    :body-style="{
      padding: '0',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
    }"
  >
    <div
      slot="header"
      class="header defaultCursor"
    >
      <span v-if="title !== 'Session Attendance'">{{ title }}</span>
      <span v-else>{{ $t('coach.leader.attendance.sessionAttendance') }}</span>
    </div>

    <div
      v-if="formattedSession"
      class="sessions"
      :class="['mobile', 'tablet'].includes($mq) ? 'mobileMode' : 'deskMode'"
    >
      <div
        v-for="(session, Skey) in formattedSession"
        :key="Skey"
        class="session"
        :class="[
          $mq,
          {
            disabledLink: session.status !== 'pending',
            disabledStatus: session.status === 'disabled',
          }
        ]"
        @click="openAttendanceModal(session)"
      >
        <div
          class="status"
          :class="{
            completed: session.status === 'completed',
            ongoing: session.status === 'pending',
            disabled: session.status === 'disabled',
            default: !['completed', 'pending', 'disabled'].includes(session.status),
          }"
        ></div>
        <div class="name">
          {{ $t(`session.${session.session_id}`) }}
        </div>
      </div>
    </div>

    <div
      v-else
      class="noData"
    >
      {{ $t('coach.leader.attendance.noData') }}
    </div>

    <AttendanceModal
      :show.sync="showAttendanceModal"
      :leader="leader"
      :session="sessionModal"
    />
  </el-card>
</template>

<script>
import { mapState } from 'vuex';
import AttendanceModal from './AttendanceModal.vue';

export default {
  components: {
    AttendanceModal,
  },

  props: {
    leader: {
      type: Object,
      default: null,
    },

    meetings: {
      type: Object,
      default: null,
    },

    title: {
      type: String,
      default: 'Session Attendance',
    },

    isDetailsView: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      // meetingStatus: [],
      // sessionList: [],
      showAttendanceModal: false,
      sessionModal: null,
      loading: false,
    };
  },

  computed: {
    ...mapState({
      user: (state) => state.user,
      attendanceList: (state) => state.coachAlerts.attendance,
    }),

    leaderUuid() {
      return this.leader.uuid;
    },

    meetingStatus() {
      if (!this.meetings || !this.meetings.meeting_status) return [];

      return this.meetings.meeting_status;
    },

    sessionList() {
      if (!this.meetings || !this.meetings.session_list) return [];

      return this.meetings.session_list.filter((el) => el !== 'on_boarding');
    },

    pendingSession() {
      if (!this.meetingStatus) return null;

      return this.meetingStatus.find((el) => el.status === 'pending');
    },

    formattedSession() {
      let session = [];
      if (this.leader && this.sessionList && this.sessionList.length) {
        const list = [...Array(this.leader.duration).keys()];
        session = list.map((number) => {
          let s = {
            session_id: `session_${number + 1}`,
          };
          const meeting = this.meetingStatus.find((met) => met.session_id === (`session_${number + 1}`));
          if (meeting) {
            s = { ...s, ...meeting };
          } else if (!this.sessionList.includes(`session_${number + 1}`)) {
            s = { ...s, status: 'disabled' };
          } else {
            s = { ...s };
          }
          return s;
        });
      }

      return session;
    },
  },

  watch: {
    attendanceList: {
      deep: true,
      handler(newValue) {
        if (newValue) {
          this.$emit('attendanceChanged');
        }
      },
    },

    pendingSession(pendingSession) {
      if (pendingSession && this.user.user.role_type === 'coach') {
        this.sessionModal = pendingSession;
        this.showAttendanceModal = true;
      }
    },
  },

  created() {
    this.$EventBus.$on('open_attendance', () => {
      const pended = this.formattedSession.find((session) => session.status === 'pending');
      if (pended) {
        this.openAttendanceModal(pended);
      }
    });
  },

  methods: {
    openAttendanceModal(session) {
      if (session.status === 'pending' && this.user.user.role_type === 'coach') {
        this.showAttendanceModal = true;
        this.sessionModal = session;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
  #attendanceCard {
    min-height: 154px;

    .header {
      font-weight: bold;
      text-align: center;
    }

    .sessions {
      display: flex;
      padding: 15px 20px 15px 35px;
      align-items: center;
      flex-wrap: wrap;

      .session {
        display: flex;
        align-items: center;
        padding: 5px 0 10px;
        box-sizing: border-box;

        &:not(.disabledLink) {
          cursor: pointer;

          &:hover {
            .name {
              text-decoration: underline;
            }
          }
        }

        &.desktop {
          width: calc(100% / 5);
        }

        &.laptop {
          width: calc(100% / 4);
        }

        &.tablet {
          width: calc(100% / 3);
        }

        &.mobile {
          width: calc(100% / 2);
        }

        .status {
          min-width: 12px;
          width: 13px;
          height: 13px;
          border-radius: 50%;
          margin-right: 10px;

          &.disabled {
            border: 1px solid #ffffff6b;
          }

          &.completed {
            border-color: #66d73f;
            background: #66d73f;
          }

          &.ongoing {
            border-color: #ffb800;
            background: #ffb800;
          }

          &.default {
            border: 1px solid #c4c4c4;
            background: transparent;
          }
        }

        &.disabledStatus {
          color: var(--themeFontColor);
          opacity: 0.4;
          cursor: not-allowed;
        }
      }
    }

    .noData {
      color: #c4c4c4;
      text-align: center;
      padding: 5px 0;
      height: 100%;
      min-height: 50px;
      display: flex;
      align-items: center;
      justify-content: center;
    }
  }
</style>

<style lang="scss">
  #attendanceCard {
    &.mobileMode {
      .el-card__header {
        border-bottom: none !important;
      }
    }
  }
</style>
